/**
 * Tipos de apoio/seed: monitoramento (QACI/IMS), divergências, riscos
 * contextuais e o checklist do Supervisor (AUDIN). Espelham o seed do protótipo.
 */

import type { NivelMaturidade } from "@caint/engine";

export type Mes = "ago" | "set" | "out";
export type ComponenteQaci = "c1" | "c2" | "c3";

export interface NotasMes {
  readonly c1: number;
  readonly c2: number;
  readonly c3: number;
}

export interface MonitoramentoSeed {
  readonly ago: NotasMes;
  readonly set: NotasMes;
  readonly out: NotasMes;
  readonly ims: readonly number[]; // 6 dimensões, nível 0–4
}

export interface Divergencia {
  readonly id: string;
  readonly item: string;
  readonly avalA: string;
  readonly notaA: string;
  readonly avalB: string;
  readonly notaB: string;
  readonly tipo: string; // 1PT | 2PT+ | CONFORMIDADE | CORE_NC | NA | PXI
  readonly status: "pendente" | "arbitrada" | string;
  readonly decisao: string | null;
}

export interface RiscoContextual {
  readonly id: string;
  readonly cat: number; // índice em CTX_CATEGORIAS
  readonly evento: string;
  readonly p: number;
  readonly i: number;
  readonly obs: string;
}

export type ResultadoSupervisao = "C" | "NC" | "NA";
/** Ponto da escala Likert de aderência (1 Não atende … 5 Atende plenamente). NA fica fora da escala. */
export type LikertPonto = 1 | 2 | 3 | 4 | 5;

export interface SupChecklistItem {
  readonly id: string;
  readonly fase: string;
  readonly criterio: string;
  readonly resultado: ResultadoSupervisao;
  readonly crit: string;
}

export interface SupItemFase {
  readonly id: string;
  readonly grupo: string;
  readonly criterio: string;
  readonly crit: string;
  readonly res: ResultadoSupervisao;
  readonly norma?: string;
}

export interface SupValidacao {
  readonly id: string;
  readonly criterio: string;
  readonly crit: string;
  readonly res: ResultadoSupervisao;
}

export interface SupAmostra {
  readonly item: string;
  readonly estrato: string;
  readonly matAval: NivelMaturidade;
  readonly matSup: NivelMaturidade;
  readonly obs: string;
}

export interface SupContestacao {
  readonly item: string;
  readonly nc: string;
  readonly original: string;
  readonly argumento: string;
  readonly analise: string;
  readonly modifica: "S" | "N";
  readonly decisao: string;
  readonly norma: string;
}

/* ---------- Supervisão AUDIN (transacional, preenchida pelo Supervisor) ---------- */

export type ParecerConfiabilidade = "Confiavel" | "Ressalvas" | "NaoConfiavel";
/** Status do item re-performado na amostra. "Validar" (ex-"Manter") = ok; "Revisar" volta ao avaliador. */
export type StatusAmostra = "Validar" | "Revisar";
/** Estratos da amostra cega (doc 09 §9.5). */
export type EstratoAmostra = "CORE" | "RiscoInerente" | "Aleatorio" | "Divergencia";

/** Resposta do supervisor a um critério de processo (PRE/CAM/VT). */
export interface SupCriterioResp {
  /** Maturidade 0-4/NA (escala unificada com o avaliador, nova). Quando
   *  presente tem precedência sobre `resultado` e `likert`. */
  readonly mat?: NivelMaturidade | null;
  /** Conformidade C/NC/NA — legado; mantida p/ backward-compat e deriva de
   *  `mat` quando ausente. */
  readonly resultado: ResultadoSupervisao | null;
  /** Nuance Likert (aba Campo, legado). Substituída por `mat`. */
  readonly likert?: LikertPonto | null;
  readonly registro?: string;
  readonly acao?: "S" | "N" | null;
}

/** Item da amostra de re-performance CEGA (Camada 2). */
export interface SupAmostraItem {
  readonly itemId: string;
  readonly estrato: EstratoAmostra;
  readonly evidencia?: string;
  readonly matSup?: NivelMaturidade | null; // nota do supervisor (cega)
  readonly obs?: string;
  readonly status?: StatusAmostra | null;
}

/** Contestação analisada pelo supervisor (Camada de contestações). */
export interface SupContestacaoItem {
  readonly id: string;
  readonly item: string;
  readonly nc?: string;
  readonly original?: ResultadoSupervisao | null;
  readonly argumento?: string;
  readonly analise?: string;
  readonly modifica?: "S" | "N" | null;
  readonly decisao?: string;
  readonly justificativa?: string;
  readonly norma?: string;
}

/** Quem prevalece numa divergência amostra × checklist. */
export type EscolhaResolucao = "avaliador" | "supervisor";

/** Decisão do supervisor para um item divergente (Camada de contestação/arbitragem). */
export interface SupResolucao {
  readonly escolha: EscolhaResolucao;
  readonly justificativa?: string;
}

/** Estado completo da supervisão de uma avaliação (blob persistido/sincronizado). */
export interface SupervisaoData {
  readonly criterios: Record<string, SupCriterioResp>; // chave = num (PRE.01/CAM.01/VT.P01)
  readonly amostra: readonly SupAmostraItem[];
  readonly contestacoes: readonly SupContestacaoItem[];
  /** Resolução das divergências por item (chave = itemId): qual nota vale no IMGQ. */
  readonly resolucoes?: Record<string, SupResolucao>;
  readonly parecer: ParecerConfiabilidade | null;
  readonly assinatura?: string;
  /**
   * Revisão do sorteio da amostra cega. Cada "refazer" incrementa este contador,
   * que entra na semente do sorteio → nova amostra a cada revisão. Ausente/0 = a
   * semente original (id da avaliação). Ver `seedAmostra` / `resetarAmostra`.
   */
  readonly amostraRev?: number;
}

/** Supervisão como devolvida pelo /sync/pull. */
export interface SupervisaoRemota {
  readonly avaliacaoId: string;
  /** Último editor do blob — NÃO é quem validou (ver `validadoPor`). */
  readonly supervisorId: string | null;
  readonly dados: SupervisaoData;
  /** Par imutável da validação: quando e por quem (doc 20 §I2/§I3). */
  readonly dataValidacao: string | null;
  readonly validadoPor: string | null;
  /** Versão vigente — o cliente devolve em `versaoBase` ao editar (doc 20 §I6). */
  readonly versao: number;
  readonly atualizadoEm: string;
}

export function supervisaoVazia(): SupervisaoData {
  return { criterios: {}, amostra: [], contestacoes: [], resolucoes: {}, parecer: null };
}

/** Shape legado de um item de amostra (antes de status/Validar). */
type SupAmostraItemLegado = SupAmostraItem & {
  readonly encaminhamento?: "Manter" | "Revisar" | StatusAmostra | null;
};

/**
 * Compatibilidade retroativa: amostras gravadas com o campo `encaminhamento` e o
 * valor `Manter` são mapeadas para `status`/`Validar` na leitura. Idempotente —
 * dados já no formato novo passam intactos.
 */
export function migrarSupervisao(dados: SupervisaoData): SupervisaoData {
  let mudou = false;
  const amostra = dados.amostra.map((item) => {
    const legado = item as SupAmostraItemLegado;
    if (legado.status === undefined && legado.encaminhamento !== undefined) {
      mudou = true;
      const { encaminhamento, ...resto } = legado;
      const status = encaminhamento === "Manter" ? "Validar" : (encaminhamento ?? null);
      return { ...resto, status } as SupAmostraItem;
    }
    return item;
  });
  return mudou ? { ...dados, amostra } : dados;
}

/* ---------- QACI/IMGR — questionário público de maturidade (Manual IMGR) ---------- */

export type NivelCargo = "Estratégico" | "Tático" | "Operacional";
/** Perfil para a ponderação do IMGR: Gestor (estrat.+tático) ×4, Liderado (oper.) ×6. */
export type PerfilImgr = "Gestor" | "Liderado";

export interface Cargo {
  readonly cargo: string;
  readonly nivel: NivelCargo;
  readonly area: string;
  readonly perfil: PerfilImgr;
}

export interface ImgrQuestao {
  readonly num: number; // 1..31
  readonly texto: string;
}
export interface ImgrComponente {
  readonly id: string; // C1..C6
  readonly nome: string;
  readonly questoes: readonly ImgrQuestao[];
}
export interface ImgrEscalaItem {
  readonly valor: number; // 0..3
  readonly rotulo: string;
  readonly desc: string;
}
export interface ImgrForm {
  readonly escala: readonly ImgrEscalaItem[];
  readonly componentes: readonly ImgrComponente[];
}

/** Faixas de classificação do IMGR (Manual IMGR §4.2.3). */
export type FaixaImgr = "Inicial" | "Básico" | "Intermediário" | "Aprimorado" | "Avançado";

/** Uma resposta do questionário (anônima por indivíduo, identifica cargo/nível/área). */
export interface ImgrRespostaPublica {
  readonly cargo: string;
  readonly nivel: NivelCargo;
  readonly area: string;
  readonly perfil: PerfilImgr;
  readonly setor?: string;
  /** Mapa num(1..31) → nota(0..3). */
  readonly respostas: Record<string, number>;
  /** Quando a resposta foi registrada (ISO) — alimenta a série temporal do dashboard. */
  readonly criadoEm?: string;
}

/* ---------- Matriz da Unidade (roster esperado p/ o IMGR) ---------- */

/** Um cargo do quadro esperado de uma unidade (denominador da cobertura). */
export interface ItemMatriz {
  readonly cargo: string;
  readonly nivel: NivelCargo;
  readonly area: string;
  readonly perfil: PerfilImgr; // derivado do nível (Operacional→Liderado)
  readonly reportaPara: string;
  readonly quantitativo: number; // headcount esperado
}

/** Matriz (roster) de uma unidade — o que se espera receber de respostas. */
export interface MatrizUnidade {
  readonly unidadeId: string;
  readonly arquivoNome: string | null;
  readonly enviadoPor: string | null;
  readonly atualizadoEm: string; // ISO
  readonly itens: readonly ItemMatriz[];
}

/**
 * Resumo da matriz de uma unidade, sem carregar o roster inteiro: quem já tem
 * quadro no sistema e de quando ele é (a tela de upload lista todas de uma vez).
 */
export interface ResumoMatrizUnidade {
  readonly unidadeId: string;
  readonly atualizadoEm: string; // ISO
  readonly cargos: number; // linhas da matriz
  readonly esperados: number; // soma dos quantitativos
}

/** Metadados do formulário público de um ciclo (devolvidos sem auth). */
export interface FormularioPublico {
  readonly token: string;
  readonly unidadeNome: string;
  readonly prazo: string | null; // ISO; = dataFim do ciclo
  readonly aberto: boolean; // dentro do prazo e ciclo aberto
  /** Cargos da unidade (da matriz) ou o catálogo global como fallback. */
  readonly cargos: readonly Cargo[];
}
